import { useRef, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { removeNotificationFromQueue } from '../redux/actionCreators';

export interface DismissableNotificationProps {
  id: number;
  queueName: string;
  duration: number;
  onCloseCallback?: () => void;
}

export function useDismissableNotification({
  id,
  queueName,
  duration,
  onCloseCallback,
}: DismissableNotificationProps) {
  const dispatch = useDispatch();
  const dismissTimeout = useRef<any>(undefined);
  const timerExpectedEnd = useRef(0);
  const pauseRemainingTime = useRef(0);

  // we only want to reset the timer when the notification is replaced, so everything below
  // only checks for the id in the memoization

  const onClose = useCallback(() => {
    dispatch(removeNotificationFromQueue(queueName, id));
    if (onCloseCallback) {
      onCloseCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const clearDismissTimeout = useCallback(() => {
    if (!dismissTimeout.current) {
      return;
    }
    clearTimeout(dismissTimeout.current);
    dismissTimeout.current = null;
    timerExpectedEnd.current = 0;
  }, []);

  const setDismissTimeout = useCallback(
    (durationOverride?: number) => {
      const effectiveDuration =
        durationOverride === undefined ? duration : durationOverride;
      clearDismissTimeout();
      dismissTimeout.current = setTimeout(onClose, effectiveDuration);
      timerExpectedEnd.current = new Date().getTime() + effectiveDuration;
      pauseRemainingTime.current = 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  const onPause = useCallback(() => {
    if (!timerExpectedEnd.current) {
      return;
    }
    pauseRemainingTime.current = Math.max(
      timerExpectedEnd.current - new Date().getTime(),
      0
    );
    if (pauseRemainingTime.current) {
      clearDismissTimeout();
    }
  }, [clearDismissTimeout]);

  const onUnpause = useCallback(() => {
    if (!pauseRemainingTime.current) {
      return;
    }
    setDismissTimeout(pauseRemainingTime.current);
  }, [setDismissTimeout]);

  useEffect(() => {
    setDismissTimeout();
    return () => clearDismissTimeout();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return { onClose, onPause, onUnpause };
}
